/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
    IAgentPlugin,
    IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_INACTIVE_BOXES_REPORT } from "../graphql/inactiveBoxesReport.query";
import { VApplication } from "../../../application";
import { GET_PRESENTATIONS } from "../graphql/getPresentations.query";

const getGraphQLModule = () => {
    const app = VApplication.getInstance();
    return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IInactiveBoxesReportMethods extends IPluginMethodMap {
    getInactiveBoxesReport(args: any): Promise<any>;
}

export class InactiveBoxesReportService implements IAgentPlugin {
    readonly methods: IInactiveBoxesReportMethods;

    constructor() {
        this.methods = {
            getInactiveBoxesReport: this.getInactiveBoxesReport.bind(this),
        };
    }

    async getInactiveBoxesReport(args: any) {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        console.log("args searchType==>", args)
        let filter = {};
        let presentationOptions: any = [],
            defaultPresentation: any = "";
        const searchType = this.determineSearchType(args?.criteria);
        if (searchType && searchType !== null) {
            if (searchType === "multi") {
                filter = this.getMultiFilter(args?.criteria);
            } else {
                filter = this.convertCriteria(args?.criteria);
            }
        } else {
            filter = {
                ...filter,
                inactive___type: {
                    "eq": "Deleted"
                }
            }
        }
        const _filter = {
            status: {
                eq: "active",
            },
        };
        try {
            if (client) {
                const presentation = await client.query({
                    query: GET_PRESENTATIONS,
                    variables: {
                        filter: _filter
                    },
                    fetchPolicy: "network-only",
                });
                console.log("args deleted==>", presentation)

                if (presentation && presentation?.data?.getPresentations?.content[0]?.id) {
                    defaultPresentation = presentation?.data?.getPresentations?.content[0]?.id;
                    presentationOptions = presentation?.data?.getPresentations?.content?.map((_item: any) => {
                        return { value: _item?.id, label: _item?.name };
                    });
                    if (searchType !== "multi") {
                        filter = {
                            ...filter,
                            presentation: {
                                eq: args?.criteria?.presentation ?? defaultPresentation
                            }
                        }
                    }
                }
                const res = await client.query({
                    query: GET_INACTIVE_BOXES_REPORT,
                    variables: {
                        filter: filter
                    },
                    fetchPolicy: "network-only",
                });

                return res
            }
        } catch (err: any) {
            let _display_message =
                err?.graphQLErrors &&
                Array.isArray(err?.graphQLErrors) &&
                err?.graphQLErrors.find((item: any) => item.extensions);
            if (_display_message?.extensions?.display_message) {
                throw Error(_display_message?.extensions?.display_message);
            }
            throw err;
        }
    }

    private determineSearchType(criteria: any) {
        if (Object.keys(criteria).length === 0) {
            return null;
        } else if (Object.keys(criteria)[0] === "0") {
            return "multi";
        } else {
            return "normal";
        }
    }

    private convertCriteria(criteria: any) {
        const result: any = {};
        for (const key in criteria) {
            result[key] = { contains: criteria[key] };
        }
        return result;
    }

    private getMultiFilter(criteria: any) {
        const transformedCriteria: any = {};
        for (const key in criteria) {
            if (criteria.hasOwnProperty(key)) {
                const { key: filterKey, filters } = criteria[key];
                const { inputData } = filters;
                const filterType = Object.keys(filters).find(key => key !== 'inputData');

                transformedCriteria[inputData.id] = {
                    [filterType.toLowerCase().replace('equal', 'eq')]: filters[filterType]
                };
            }
        }
        return transformedCriteria;
    }

}
